import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

interface DisplayResult {
  id: string;
  title: string;
  subtitle?: string;
  url: string;
}

interface SequenceResult {
  id: string;
  title: string;
  subtitle?: string;
  url: string;
  imageUrl?: string;
}

interface SectionedSearchResultsProps {
  displayResults: DisplayResult[];
  sequenceResults: SequenceResult[];
  searchQuery: string;
  isLoading?: boolean;
  onResultClick?: () => void;
  selectedIndex?: number;
}

export const SectionedSearchResults: React.FC<SectionedSearchResultsProps> = ({
  displayResults,
  sequenceResults,
  searchQuery,
  isLoading = false,
  onResultClick,
  selectedIndex = -1
}) => {
  const navigate = useNavigate();

  const handleResultClick = (url: string) => {
    if (onResultClick) onResultClick();
    navigate(url);
  };

  const handleViewAllDisplays = () => {
    if (onResultClick) onResultClick();
    navigate(`/displays?q=${encodeURIComponent(searchQuery)}`);
  };

  const handleViewAllSequences = () => {
    if (onResultClick) onResultClick();
    navigate(`/sequences/search?q=${encodeURIComponent(searchQuery)}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <div className="animate-spin h-5 w-5 border-2 border-current border-t-transparent rounded-full mr-2" />
        Searching...
      </div>
    );
  }

  const hasDisplays = displayResults.length > 0;
  const hasSequences = sequenceResults.length > 0;
  const hasAnyResults = hasDisplays || hasSequences;

  if (!hasAnyResults) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <div className="flex items-center justify-center gap-2 mb-2">
          <MapPin size={24} className="opacity-50" />
          <Sparkles size={24} className="opacity-50" />
        </div>
        <p>No results found for "{searchQuery}"</p>
        <p className="text-sm mt-1">Try different keywords or browse by category</p>
      </div>
    );
  }

  // Calculate global index for keyboard navigation
  let globalIndex = -1;

  return (
    <div className="space-y-4">
      {/* Display Results Section */}
      {hasDisplays && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <MapPin size={16} className="text-green-500" />
              <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                Displays
              </h3>
              <Badge variant="secondary" className="text-xs">
                {displayResults.length}
              </Badge>
            </div>
          </div>

          <div className="space-y-1">
            {displayResults.slice(0, 3).map((result) => {
              globalIndex++;
              const isSelected = selectedIndex === globalIndex;
              return (
                <button
                  key={result.id}
                  onClick={() => handleResultClick(result.url)}
                  className={cn(
                    "w-full p-3 text-left rounded-lg transition-colors flex items-center space-x-3 hover:bg-secondary",
                    isSelected && "bg-secondary"
                  )}
                >
                  <MapPin size={16} className="text-green-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{result.title}</div>
                    {result.subtitle && (
                      <div className="text-sm text-muted-foreground truncate">
                        {result.subtitle}
                      </div>
                    )}
                  </div>
                  <Badge variant="outline" className="text-xs bg-green-500/10 text-green-700 border-green-200">
                    Display
                  </Badge>
                </button>
              );
            })}
          </div>

          {displayResults.length > 3 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleViewAllDisplays}
              className="w-full mt-2 text-green-600 hover:text-green-700 hover:bg-green-50"
            >
              View all {displayResults.length} display results
              <ArrowRight size={14} className="ml-2" />
            </Button>
          )}
        </div>
      )}

      {/* Separator between sections */}
      {hasDisplays && hasSequences && <Separator />}

      {/* Sequence Results Section */}
      {hasSequences && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-blue-500" />
              <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                Sequences
              </h3>
              <Badge variant="secondary" className="text-xs">
                {sequenceResults.length}
              </Badge>
            </div>
          </div>

          <div className="space-y-1">
            {sequenceResults.slice(0, 3).map((result) => {
              globalIndex++;
              const isSelected = selectedIndex === globalIndex;
              return (
                <button
                  key={result.id}
                  onClick={() => handleResultClick(result.url)}
                  className={cn(
                    "w-full p-3 text-left rounded-lg transition-colors flex items-center space-x-3 hover:bg-secondary",
                    isSelected && "bg-secondary"
                  )}
                >
                  {/* Album artwork or fallback icon */}
                  {result.imageUrl ? (
                    <div className="flex-shrink-0 w-12 h-12 rounded overflow-hidden bg-gray-100">
                      <img
                        src={result.imageUrl}
                        alt={result.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="flex-shrink-0 w-12 h-12 rounded overflow-hidden bg-gray-100 flex items-center justify-center">
                      <Sparkles size={24} className="text-blue-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{result.title}</div>
                    {result.subtitle && (
                      <div className="text-sm text-muted-foreground truncate">
                        {result.subtitle}
                      </div>
                    )}
                  </div>
                  <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-700 border-blue-200">
                    Sequence
                  </Badge>
                </button>
              );
            })}
          </div>

          {sequenceResults.length > 3 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleViewAllSequences}
              className="w-full mt-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              View all {sequenceResults.length} sequence results
              <ArrowRight size={14} className="ml-2" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
